/**
 * Guilds (dono, 2026-10-22; spec em MobileDocs servidor/guilds_painel.md): cards horizontais com as guilds dos
 * personagens da conta (com o emblema, se tiver). Clicar abre o placar (membros, pontos, pontos de guerra, kills) e a
 * lista de membros, com o(s) personagem(ns) da conta em destaque. Mestre da guild: envio do emblema -- so' PNG no
 * tamanho exato do jogo, 1 troca a cada 7 dias; o arquivo vai pro patch e o jogo baixa ao abrir.
 */
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Crown, Flag, ImageUp, Loader2, Shield, Upload } from 'lucide-react';
import { CLASSES_PT } from '@/features/Admin/labels';
import {
  getGuild,
  getMyGuilds,
  uploadGuildEmblem,
  type GuildCard,
  type GuildDetail,
} from '@/shared/services/guildService';
import { errMsg } from './NxModal';

type Notice = { tone: 'ok' | 'error'; text: string } | null;
type Picked = { file: File; preview: string } | null;

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const fmt = (n: number) => (n ?? 0).toLocaleString('pt-BR');

function Emblem({ url, size }: { url: string | null; size: 'sm' | 'lg' }) {
  return (
    <span className={`nxd-emblem nxd-emblem--${size}`}>
      {url ? <img src={url} alt="" loading="lazy" /> : <Shield size={size === 'lg' ? 34 : 26} />}
    </span>
  );
}

/** Confere no navegador antes de enviar (a API confere de novo). */
function checkPng(file: File, w: number, h: number, maxBytes: number): Promise<string | null> {
  if (file.type !== 'image/png' || !file.name.toLowerCase().endsWith('.png')) {
    return Promise.resolve('Envie um arquivo PNG.');
  }
  if (file.size > maxBytes) return Promise.resolve(`Arquivo maior que ${Math.floor(maxBytes / 1024)} KB.`);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(
        img.naturalWidth === w && img.naturalHeight === h
          ? null
          : `O emblema precisa ter exatamente ${w}x${h} pixels (o seu tem ${img.naturalWidth}x${img.naturalHeight}).`,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('Não foi possível ler a imagem. Envie um PNG válido.');
    };
    img.src = url;
  });
}

export default function NextGuildsPage() {
  const [guilds, setGuilds] = useState<GuildCard[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<GuildDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [picked, setPicked] = useState<Picked>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadGuilds = () =>
    getMyGuilds()
      .then((list) => {
        setGuilds(list);
        if (list.length === 1) setSelected((cur) => cur ?? list[0].guildNo);
      })
      .catch(() => setGuilds([]));

  const loadDetail = (guildNo: number) => {
    setLoadingDetail(true);
    getGuild(guildNo)
      .then(setDetail)
      .catch((e) => {
        setDetail(null);
        setNotice({ tone: 'error', text: errMsg(e, 'Não foi possível abrir a guild.') });
      })
      .finally(() => setLoadingDetail(false));
  };

  useEffect(() => {
    void loadGuilds();
  }, []);

  useEffect(() => {
    if (selected === null) return;
    setPicked(null);
    loadDetail(selected);
  }, [selected]);

  // libera a pre-visualizacao ao trocar/sair
  useEffect(() => () => {
    if (picked) URL.revokeObjectURL(picked.preview);
  }, [picked]);

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !detail) return;
    const r = detail.emblemRules;
    const problem = await checkPng(file, r.width, r.height, r.maxBytes);
    if (problem) {
      setPicked(null);
      setNotice({ tone: 'error', text: problem });
      return;
    }
    setNotice(null);
    setPicked({ file, preview: URL.createObjectURL(file) });
  };

  const send = async () => {
    if (!picked || !detail) return;
    setBusy(true);
    try {
      const res = await uploadGuildEmblem(detail.guildNo, picked.file);
      setNotice({ tone: 'ok', text: res?.message || 'Emblema enviado! Após reiniciar o jogo, o emblema estará disponível.' });
      setPicked(null);
      loadDetail(detail.guildNo);
      void loadGuilds();
    } catch (e: any) {
      setNotice({ tone: 'error', text: errMsg(e, 'Não foi possível enviar o emblema.') });
    } finally {
      setBusy(false);
    }
  };

  const rules = detail?.emblemRules;
  const nextAt = detail && detail.emblemWaitSeconds > 0 ? new Date(Date.now() + detail.emblemWaitSeconds * 1000) : null;

  return (
    <div className="nxd-stack">
      <header className="nxd-heading">
        <p className="nx-eyebrow">Painel do jogador</p>
        <h1>Guilds</h1>
      </header>

      {notice && <p className={`nxd-notice nxd-notice--${notice.tone}`}>{notice.text}</p>}

      {guilds === null ? (
        <div className="nxd-loading">
          <Loader2 size={26} className="nxb-spin" />
        </div>
      ) : guilds.length === 0 ? (
        <div className="nxd-card nxd-empty">Nenhum personagem da conta está em uma guild.</div>
      ) : (
        <div className="nxd-guilds" role="list">
          {guilds.map((g) => (
            <button
              key={g.guildNo}
              type="button"
              role="listitem"
              className={`nxd-guild${selected === g.guildNo ? ' is-active' : ''}`}
              onClick={() => setSelected(g.guildNo)}
            >
              <Emblem url={g.emblemUrl} size="sm" />
              <span className="nxd-guild__body">
                <b title={g.name}>{g.name}</b>
                <small>
                  {fmt(g.members)} {g.members === 1 ? 'membro' : 'membros'} · {fmt(g.warKills)} kills
                </small>
                <small className="nxd-guild__mine" title={g.myChars.join(', ')}>
                  {g.isMaster && <Crown size={12} />} {g.myChars.join(', ')}
                </small>
              </span>
            </button>
          ))}
        </div>
      )}

      {selected !== null && (loadingDetail && !detail ? (
        <div className="nxd-loading">
          <Loader2 size={26} className="nxb-spin" />
        </div>
      ) : detail && (
        <section className="nxd-card nxd-guildinfo">
          <div className="nxd-guildinfo__head">
            <Emblem url={detail.emblemUrl} size="lg" />
            <div>
              <h2>{detail.name}</h2>
              <p>
                Nível {detail.level}
                {detail.isMaster && (
                  <span className="nxd-guildinfo__master">
                    <Crown size={13} /> Você é o mestre
                  </span>
                )}
              </p>
            </div>
          </div>

          <dl className="nxd-guildinfo__stats">
            <div>
              <dt>Membros</dt>
              <dd>{fmt(detail.members)}</dd>
            </div>
            <div>
              <dt>Pontos da guild</dt>
              <dd>{fmt(detail.point)}</dd>
            </div>
            <div>
              <dt>Pontos de guerra</dt>
              <dd>{fmt(detail.warPoints)}</dd>
            </div>
            <div>
              <dt>Kills na guerra</dt>
              <dd>{fmt(detail.warKills)}</dd>
            </div>
          </dl>

          {detail.isMaster && rules && (
            <div className="nxd-emblemup">
              <div className="nxd-emblemup__preview">
                {picked ? <img src={picked.preview} alt="" /> : <Emblem url={detail.emblemUrl} size="lg" />}
              </div>
              <div className="nxd-emblemup__body">
                <h3>
                  <ImageUp size={16} /> Emblema da guild
                </h3>
                <ul>
                  <li>
                    Arquivo <b>PNG</b> com exatamente <b>{rules.width}x{rules.height} pixels</b>.
                  </li>
                  <li>Fundo transparente recomendado (o jogo mostra o emblema ao lado do nome da guild).</li>
                  <li>
                    Até {Math.floor(rules.maxBytes / 1024)} KB. Pode trocar 1 vez a cada {rules.cooldownDays} dias.
                  </li>
                </ul>
                {nextAt ? (
                  <p className="nxd-emblemup__wait">Próxima troca liberada em {DATE_TIME.format(nextAt)}.</p>
                ) : (
                  <div className="nxd-emblemup__actions">
                    <input ref={fileRef} type="file" accept="image/png" hidden onChange={(e) => void pick(e)} />
                    <button type="button" className="nxd-btn" onClick={() => fileRef.current?.click()} disabled={busy}>
                      <Upload size={15} /> Escolher PNG
                    </button>
                    <button
                      type="button"
                      className="nxd-btn nxd-btn--primary"
                      onClick={() => void send()}
                      disabled={busy || !picked}
                    >
                      {busy ? <Loader2 size={15} className="nxb-spin" /> : <Flag size={15} />}
                      {busy ? 'Enviando...' : 'Enviar emblema'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="nxd-members">
            <div className="nxd-members__row nxd-members__row--head">
              <span>Membro</span>
              <span>Classe</span>
              <span>Nível</span>
              <span>Kills</span>
            </div>
            {detail.memberList.map((m) => (
              <div key={m.name} className={`nxd-members__row${m.mine ? ' is-mine' : ''}`}>
                <span className="nxd-members__name">
                  <i className={`nxd-members__dot${m.online ? ' is-on' : ''}`} title={m.online ? 'Online' : 'Offline'} />
                  {m.master && <Crown size={13} className="nxd-members__crown" />}
                  <b title={m.name}>{m.name}</b>
                  {m.mine && <em>seu</em>}
                </span>
                <span>{CLASSES_PT[m.classId] || '—'}</span>
                <span>{m.overlord > 0 ? `${m.level} · OL ${m.overlord}` : m.level}</span>
                <span>{fmt(m.warKills)}</span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
